
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/auth';
import { AiPrayerAssistantService } from '@/services/AiPrayerAssistantService';
import { useAiPrayerMigration } from './useAiPrayerMigration';
import type { Database } from '@/integrations/supabase/types';

type AiPrayerAssistantSettings = Database['public']['Tables']['ai_prayer_assistant_settings']['Row'];
type AiPrayerAssistantSettingsUpdate = Database['public']['Tables']['ai_prayer_assistant_settings']['Update'];

export function useAiPrayerAssistant() {
  const { user } = useAuth();
  const { migrationCompleted, getFallbackSettings } = useAiPrayerMigration();
  
  const [settings, setSettings] = useState<AiPrayerAssistantSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.id && migrationCompleted) {
      loadSettings();
    }
  }, [user?.id, migrationCompleted]);

  const loadSettings = async () => {
    if (!user?.id) return;

    try {
      setIsLoading(true);
      setError(null);

      let userSettings = await AiPrayerAssistantService.getAssistantSettings(user.id);

      // If no settings exist, create default ones
      if (!userSettings) {
        const fallback = getFallbackSettings().aiPrayerSettings;
        userSettings = await AiPrayerAssistantService.createAssistantSettings({
          user_id: user.id,
          title: fallback.title || 'Let Me Pray For You',
          logo_url: fallback.logo || null,
          avatar_url: fallback.avatar || null,
          is_enabled: fallback.isEnabled !== false,
          is_minimized: fallback.isMinimized || false,
          theme_settings: fallback.themeSettings || {}
        });
      }

      setSettings(userSettings);
    } catch (err: any) {
      console.error('Error loading assistant settings:', err);
      setError(err.message);
      
      // Use fallback settings
      const fallback = getFallbackSettings().aiPrayerSettings;
      setSettings({
        id: 'fallback',
        user_id: user.id,
        title: fallback.title || 'Let Me Pray For You',
        logo_url: fallback.logo || null,
        avatar_url: fallback.avatar || null,
        is_enabled: fallback.isEnabled !== false,
        is_minimized: fallback.isMinimized || false,
        theme_settings: fallback.themeSettings || {},
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateSettings = async (updates: AiPrayerAssistantSettingsUpdate) => {
    if (!user?.id || !settings) return;

    try {
      const updatedSettings = await AiPrayerAssistantService.updateAssistantSettings(
        user.id,
        updates
      );
      setSettings(updatedSettings);
    } catch (err: any) {
      console.error('Error updating assistant settings:', err);
      setError(err.message);
    }
  };

  return {
    settings,
    isLoading,
    error,
    updateSettings,
    reloadSettings: loadSettings
  };
}
